import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import {
  VALID_SOURCES,
  type CaseRecord,
  type Direction,
  type ReceiptData,
  type SourceType,
} from "../types";
import { formatDate, formatTime } from "../lib/formatDate";

interface Props {
  caseId: string;
  onRecordCreated: (record: CaseRecord) => void;
  // "receipt" switches the canvas into receipt-scan mode: the header
  // copy changes, and any record created from this mount is auto-
  // tagged "receipt" and gets a "Receipt — …" subject if the server
  // detected receipt metadata during OCR.
  mode?: "normal" | "receipt";
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  GBP: "£",
  EUR: "€",
  CAD: "C$",
  AUD: "A$",
  JPY: "¥",
};

function buildReceiptSubject(r: ReceiptData): string {
  const parts: string[] = ["Receipt"];
  if (r.vendor) parts.push(r.vendor);
  if (r.amount !== null && Number.isFinite(r.amount)) {
    const sym =
      (r.currency && CURRENCY_SYMBOLS[r.currency.toUpperCase()]) ??
      (r.currency ?? "");
    parts.push(`${sym}${r.amount!.toFixed(2)}`);
  }
  if (r.date) {
    parts.push(formatDate(r.date, "short") || r.date);
  }
  return parts.join(" — ");
}

type JobStatus = "uploading" | "saving" | "done" | "error";

interface Job {
  id: string;
  name: string;
  status: JobStatus;
  error?: string;
  // Set for image jobs; absent for text-paste jobs (no thumbnail to show).
  thumbUrl?: string;
  // Surfaces after upload when the file lands but OCR/text extraction
  // failed. The record is still created so the image isn't lost, but
  // the body will be empty until "Re-run OCR" succeeds.
  extractionWarning?: string;
}

function isImageFile(f: File): boolean {
  return (f.type || "").startsWith("image/");
}

function resolveRecordDate(extractedDate: string | null): string {
  // If OCR returned a YYYY-MM-DD-looking date, anchor the record to midnight
  // UTC of that day. Otherwise the record gets the paste timestamp.
  if (extractedDate && /^\d{4}-\d{2}-\d{2}/.test(extractedDate)) {
    const iso = extractedDate.slice(0, 10) + "T00:00:00Z";
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return new Date().toISOString();
}

function truncate(s: string, n: number): string {
  const clean = s.replace(/\s+/g, " ").trim();
  return clean.length <= n ? clean : clean.slice(0, n - 1) + "…";
}

function autoTitle(text: string | null, recordIso: string): string {
  if (text) {
    const firstLine = text
      .split(/\n/)
      .map((l) => l.trim())
      .find((l) => l.length > 0);
    if (firstLine) return truncate(firstLine, 80);
  }
  const datePart = formatDate(recordIso, "short");
  if (!datePart) return "Screenshot";
  const timePart = formatTime(recordIso);
  return timePart
    ? `Screenshot — ${datePart} · ${timePart}`
    : `Screenshot — ${datePart}`;
}

// macOS clipboard paste names: tempImageE18KsS.png. These should never
// reach the record subject — strip them before saving.
function isTempPasteFilename(s: string | null | undefined): boolean {
  if (!s) return false;
  if (/^temp[A-Za-z0-9]+\.(png|jpg|jpeg|gif|webp)$/i.test(s)) return true;
  if (/^image\.(png|jpg|jpeg|gif|webp)$/i.test(s)) return true;
  if (/^Pasted image /i.test(s)) return true;
  return false;
}

function coerceSource(s: string | null): SourceType {
  if (s && (VALID_SOURCES as readonly string[]).includes(s))
    return s as SourceType;
  return "other";
}

function coerceDirection(d: string | null): Direction {
  return d === "outbound" ? "outbound" : "inbound";
}

export default function PasteCanvas({
  caseId,
  onRecordCreated,
  mode = "normal",
}: Props) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [dragging, setDragging] = useState(false);
  const isReceipt = mode === "receipt";

  const processOne = useCallback(
    async (file: File) => {
      const jobId = crypto.randomUUID();
      const thumbUrl = URL.createObjectURL(file);
      const displayName =
        file.name && file.name !== "image.png" ? file.name : "screenshot";

      setJobs((prev) => [
        ...prev,
        { id: jobId, name: displayName, status: "uploading", thumbUrl },
      ]);

      try {
        const upload = await api.uploadFile(caseId, file);
        if (upload.extraction_error) {
          console.warn(
            `[paste] OCR reported an error: ${upload.extraction_error}`,
          );
        }

        setJobs((prev) =>
          prev.map((j) =>
            j.id === jobId
              ? {
                  ...j,
                  status: "saving",
                  extractionWarning:
                    upload.extraction_error ??
                    (!upload.extracted.body_text && !upload.extracted.ocr_text
                      ? "No text could be read from the image — it may be blank or unreadable."
                      : undefined),
                }
              : j,
          ),
        );

        const recordDate = resolveRecordDate(
          upload.extracted.receipt_data?.date ??
            upload.extracted.record_date,
        );
        const bodyText =
          upload.extracted.body_text ?? upload.extracted.ocr_text ?? null;
        const receipt = upload.extracted.receipt_data ?? null;
        const extractedSubject = upload.extracted.subject?.trim();
        const subject = receipt
          ? buildReceiptSubject(receipt)
          : extractedSubject && !isTempPasteFilename(extractedSubject)
            ? extractedSubject
            : autoTitle(bodyText, recordDate);
        // In receipt-scan mode we force the tag even if no receipt
        // metadata was detected — the user explicitly said "this is a
        // receipt" by choosing that capture flow. If metadata WAS
        // detected the backend will add the tag anyway.
        const tags = receipt || isReceipt ? "receipt" : null;

        const { record } = await api.createRecord(caseId, {
          record_date: recordDate,
          direction: coerceDirection(upload.extracted.direction),
          source_type: coerceSource(upload.extracted.source_type),
          from_addr: upload.extracted.from_addr,
          to_addr: upload.extracted.to_addr,
          subject,
          body_text: bodyText,
          ocr_text: upload.extracted.ocr_text,
          file_r2_key: upload.file_r2_key,
          file_name: upload.file_name,
          file_mime_type: upload.file_mime_type,
          receipt_data: receipt,
          tags,
          extraction_confidence: upload.extracted.extraction_confidence,
          date_source: upload.extracted.date_source,
        });

        // True content duplicate is dropped server-side (no record returned).
        if (record) onRecordCreated(record);

        setJobs((prev) =>
          prev.map((j) => (j.id === jobId ? { ...j, status: "done" } : j)),
        );
        // Keep the tile on screen longer when there's a warning so the
        // user actually reads "OCR returned no text…" before it
        // dismisses. Clean success fades in 1.8s as before.
        const lingerMs =
          (() => {
            return upload.extraction_error ||
              (!upload.extracted.body_text && !upload.extracted.ocr_text)
              ? 7000
              : 1800;
          })();
        window.setTimeout(() => {
          setJobs((prev) => prev.filter((j) => j.id !== jobId));
          URL.revokeObjectURL(thumbUrl);
        }, lingerMs);
      } catch (err) {
        setJobs((prev) =>
          prev.map((j) =>
            j.id === jobId
              ? { ...j, status: "error", error: (err as Error).message }
              : j,
          ),
        );
      }
    },
    [caseId, onRecordCreated],
  );

  const processFiles = useCallback(
    (files: File[]) => {
      const images = files.filter(isImageFile);
      // Fire them all off in parallel — each runs through upload → OCR → save
      // independently and appears in the timeline as it finishes.
      for (const f of images) void processOne(f);
    },
    [processOne],
  );

  const processText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const jobId = crypto.randomUUID();
      const displayName = `Pasted text — ${truncate(trimmed, 40)}`;

      setJobs((prev) => [
        ...prev,
        { id: jobId, name: displayName, status: "saving" },
      ]);

      try {
        const recordDate = new Date().toISOString();
        const subject = autoTitle(trimmed, recordDate);

        const { record } = await api.createRecord(caseId, {
          record_date: recordDate,
          direction: "inbound",
          source_type: "other",
          subject,
          body_text: trimmed,
        });

        // True content duplicate is dropped server-side (no record returned).
        if (record) onRecordCreated(record);
        setJobs((prev) =>
          prev.map((j) => (j.id === jobId ? { ...j, status: "done" } : j)),
        );
        window.setTimeout(() => {
          setJobs((prev) => prev.filter((j) => j.id !== jobId));
        }, 1800);
      } catch (err) {
        setJobs((prev) =>
          prev.map((j) =>
            j.id === jobId
              ? { ...j, status: "error", error: (err as Error).message }
              : j,
          ),
        );
      }
    },
    [caseId, onRecordCreated],
  );

  // Global paste listener. Only mounted while the timeline tab is active
  // (this component unmounts with the tab), so it can't collide with the
  // RecordForm's dropzone which lives in a different tab.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      // Don't intercept pastes aimed at form fields / editable regions —
      // the user is typing into something and expects normal paste behaviour.
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }

      const cd = e.clipboardData;
      if (!cd) return;

      // Detect images first. Check both the item's stated type and the
      // resulting File's type — some browsers/OSes leave one or the other
      // empty for native-app clipboard images.
      const files: File[] = [];
      for (const item of cd.items) {
        if (item.kind !== "file") continue;
        const itemType = item.type || "";
        const f = item.getAsFile();
        if (!f) continue;
        if (itemType.startsWith("image/") || isImageFile(f)) files.push(f);
      }

      if (files.length > 0) {
        e.preventDefault();
        processFiles(files);
        return;
      }

      // No images — fall back to text. Useful for pasting SMS bodies,
      // email copies, portal messages etc. as plain-text records.
      const text = cd.getData("text/plain") ?? "";
      if (text.trim().length >= 3) {
        e.preventDefault();
        void processText(text);
      }
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [processFiles, processText]);

  // Revoke all thumb object URLs on unmount so we don't leak blobs.
  useEffect(() => {
    return () => {
      for (const j of jobs) {
        if (j.thumbUrl) URL.revokeObjectURL(j.thumbUrl);
      }
    };
    // Intentionally empty — capture jobs at teardown via the latest state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`paste-canvas${dragging ? " dragging" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
      }}
      tabIndex={0}
    >
      <div className="paste-canvas-inner">
        <div className="paste-canvas-icon" aria-hidden>
          {isReceipt ? "🧾" : "📋"}
        </div>
        <div className="paste-canvas-copy">
          <div className="paste-canvas-title">
            {isReceipt
              ? "Scan a receipt or invoice"
              : "Paste screenshots or text"}
          </div>
          <div className="paste-canvas-hint">
            {isReceipt ? (
              <>
                Take a photo of your receipt or paste a screenshot — we'll
                extract the amount, date, and vendor automatically. Press{" "}
                <kbd>⌘V</kbd> / <kbd>Ctrl+V</kbd> or drop an image here.
              </>
            ) : (
              <>
                Press <kbd>⌘V</kbd> / <kbd>Ctrl+V</kbd> or drop images
                here. Screenshots get read for text; plain text
                becomes a text-only record. Either way it lands in
                the timeline immediately.
              </>
            )}
          </div>
        </div>
      </div>

      {jobs.length > 0 && (
        <div className="paste-jobs">
          {jobs.map((j) => (
            <div key={j.id} className={`paste-job paste-job-${j.status}`}>
              {j.thumbUrl ? (
                <img
                  src={j.thumbUrl}
                  className="paste-job-thumb"
                  alt=""
                  loading="lazy"
                />
              ) : (
                <div
                  className="paste-job-thumb paste-job-thumb-text"
                  aria-hidden
                >
                  📝
                </div>
              )}
              <div className="paste-job-meta">
                <div className="paste-job-name">{j.name}</div>
                <div className="paste-job-status">
                  {j.status === "uploading" && (
                    <>
                      <span className="spinner" /> Reading screenshot…
                    </>
                  )}
                  {j.status === "saving" && (
                    <>
                      <span className="spinner" /> Saving record…
                    </>
                  )}
                  {j.status === "done" &&
                    (j.extractionWarning
                      ? `✓ Added — ⚠ ${j.extractionWarning}`
                      : "✓ Added to timeline")}
                  {j.status === "error" && `⚠ ${j.error ?? "Failed"}`}
                </div>
              </div>
              {j.status === "error" && (
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() =>
                    setJobs((prev) => prev.filter((x) => x.id !== j.id))
                  }
                  aria-label="Dismiss"
                  title="Dismiss"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
